/**
 * SponsorBanner — public banner slot with slider + dashed placeholder fallback.
 *
 * Variants:
 *  - placement="home_main"  → wide (ratio ~6:1, full-width 1200×200 ideal)
 *  - placement="sidebar"    → square (1:1, 600×600 ideal)
 *
 * Behaviour:
 *  - Up to 3 active banners auto-rotate every 3 seconds
 *  - When no banners are active → big dashed placeholder linking /contatti/collabora
 *  - When banners ARE active → below the slider shows a small red strip
 *    "Vuoi mettere qui la pubblicità? Clicca qui" linking /contatti/collabora
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Megaphone, ArrowRight } from "lucide-react";

const API = `${window.__BACKEND__}/api`;
const ROTATION_MS = 3000;
const COLLAB_URL = "/contatti/collabora";

const PLACEMENT_LABEL = {
  home_main: "Metti qui il banner della tua idea, azienda o servizio",
  sidebar: "Metti qui il tuo annuncio",
  servizi: "Vuoi mettere qui il tuo servizio?",
};

const PLACEMENT_DESC = {
  home_main:
    "Spazio sponsor premium: la box visibile a tutti gli host che visitano la home. Clicca per inviare la tua proposta — risposta entro 24h.",
  sidebar:
    "Spot square nella sidebar — sempre visibile mentre l'utente scorre. Posizione ad alto valore. Clicca per chiedere info.",
  servizi:
    "Banner ultra-wide 2260×750 nella pagina Servizi: visto da ogni host che sceglie i tool giusti. Contattaci per riservare uno slot.",
};

export function SponsorBanner({ placement = "home_main" }) {
  const [banners, setBanners] = useState([]);
  const [active, setActive] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/banners?placement=${placement}`)
      .then((r) => {
        if (mounted) setBanners((r.data?.items || []).slice(0, 5));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [placement]);

  // Auto-rotation
  useEffect(() => {
    if (banners.length <= 1) return undefined;
    timer.current = setInterval(() => {
      setActive((i) => (i + 1) % banners.length);
    }, ROTATION_MS);
    return () => clearInterval(timer.current);
  }, [banners.length]);

  // Fire-and-forget click tracker — uses navigator.sendBeacon when available for
  // reliability across new-tab navigation, falls back to fetch keepalive.
  const trackClick = (banner) => {
    if (!banner?.id) return;
    const url = `${API}/banners/${encodeURIComponent(banner.id)}/click`;
    const payload = JSON.stringify({ referrer: typeof window !== "undefined" ? window.location.pathname : null });
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
    } catch { /* fallthrough */ }
    try {
      fetch(url, { method: "POST", body: payload, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
    } catch { /* silent */ }
  };

  const isWide = placement === "home_main";
  const isServizi = placement === "servizi";

  // ---------- ACTIVE BANNERS ----------
  if (banners.length > 0) {
    const current = banners[active] || banners[0];
    const ratio = isServizi
      ? "aspect-[226/75]"        // servizi ultra-wide: 2260×750
      : isWide
      ? "aspect-[6/1]"           // wide hero: 1200×200
      : "aspect-square";         // sidebar/square: 1:1
    return (
      <div
        className={isWide || isServizi ? "py-2 md:py-3" : ""}
        data-testid={`sponsor-banner-${placement}-active`}
      >
        <div className={isWide || isServizi ? "max-w-[1200px] mx-auto" : ""}>
          {/* Slider */}
          <a
            href={current.target_url}
            target="_blank"
            rel="noopener sponsored"
            onClick={() => trackClick(current)}
            onAuxClick={() => trackClick(current)}
            className={`relative block w-full ${ratio} rounded-2xl overflow-hidden border border-white/12 hover:border-white/25 transition-all bg-black/40`}
            data-testid={`sponsor-banner-slide-${active}`}
            aria-label={current.title || "Sponsor"}
          >
            {banners.map((b, i) => (
              <img
                key={b.id}
                src={b.image_url}
                alt={b.title || "Sponsor"}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                  i === active ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
            {banners.length > 1 && (
              <div className="absolute bottom-2 right-2 flex gap-1.5">
                {banners.map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                      i === active ? "bg-white" : "bg-white/35"
                    }`}
                  />
                ))}
              </div>
            )}
          </a>
          {/* Small red alert strip */}
          <Link
            to={COLLAB_URL}
            className="mt-1.5 block text-center text-[11px] md:text-[11.5px] font-bold tracking-wide bg-[#d40000] hover:bg-[#b00000] text-white py-1.5 px-3 rounded-lg transition-colors"
            data-testid={`sponsor-banner-${placement}-alert`}
          >
            ▸ Vuoi mettere qui la pubblicità? Clicca qui
          </Link>
        </div>
      </div>
    );
  }

  // ---------- EMPTY → DASHED PLACEHOLDER (light theme, mockup) ----------
  const dashedStyle = {
    background: "#E2E5EA",
    border: "2px dashed #C3C8D0",
  };

  if (isWide || isServizi) {
    return (
      <section data-testid={`sponsor-banner-${placement}-empty`}>
        <Link
          to={COLLAB_URL}
          className="block w-full rounded-[20px] transition-opacity hover:opacity-90 overflow-hidden"
          style={dashedStyle}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-8 md:py-10" style={{ aspectRatio: isServizi ? undefined : "4 / 1", minHeight: 140 }}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-none" style={{ background: "#FFFFFF", color: "#4A505B", border: "1px solid #D5D8DE" }}>
                <Megaphone size={20} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.1em] font-extrabold mb-1" style={{ color: "#5B616C" }}>Spazio sponsor</div>
                <div className="font-bold text-[16px] md:text-[18px] leading-tight" style={{ color: "#111318" }}>{PLACEMENT_LABEL[placement]}</div>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 self-start sm:self-center rounded-full font-extrabold text-[14px] px-5 py-3 flex-none" style={{ background: "#111318", color: "#FFFFFF" }}>
              Invia la tua proposta <ArrowRight size={16} />
            </div>
          </div>
        </Link>
      </section>
    );
  }

  // sidebar square dashed placeholder (light)
  return (
    <Link
      to={COLLAB_URL}
      className="block aspect-square rounded-[12px] p-5 transition-opacity hover:opacity-90 flex flex-col justify-center text-center"
      style={dashedStyle}
      data-testid={`sponsor-banner-${placement}-empty`}
    >
      <div className="font-bold text-[15px] leading-snug mb-2" style={{ color: "#111318" }}>
        Metti qui il banner della tua idea, azienda o servizio
      </div>
      <p className="text-[13px] leading-relaxed mb-3" style={{ color: "#4A505B" }}>
        Spot quadrato sempre visibile nella sidebar della home.
      </p>
      <div className="inline-flex items-center justify-center gap-1.5 text-[13px] font-extrabold px-3 py-2 rounded-full self-center" style={{ background: "#111318", color: "#FFFFFF" }}>
        Clicca qui <ArrowRight size={12} />
      </div>
    </Link>
  );
}

export default SponsorBanner;
